import { useState, useEffect, useRef } from 'react';
import api from '../api';

// Vilnius centras kaip numatytasis
const DEFAULT_CENTER = [54.6872, 25.2797];
const DEFAULT_ZOOM   = 13;

export default function CameraMap() {
  const mapRef     = useRef(null);
  const leafletMap = useRef(null);
  const markers    = useRef([]);
  const [cameras,  setCameras]  = useState([]);
  const [clients,  setClients]  = useState([]);
  const [selCam,   setSelCam]   = useState(null);
  const [filter,   setFilter]   = useState('');
  const [mapReady, setMapReady] = useState(false);

  // Load Leaflet dynamically
  useEffect(() => {
    if (window.L) { setMapReady(true); return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setMapReady(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    api.get('/cameras').then(r => setCameras(r.data)).catch(() => {});
    api.get('/clients').then(r => setClients(r.data)).catch(() => {});
  }, []);

  // Init map
  useEffect(() => {
    if (!mapReady || !mapRef.current || leafletMap.current) return;
    const L = window.L;

    leafletMap.current = L.map(mapRef.current).setView(DEFAULT_CENTER, DEFAULT_ZOOM);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(leafletMap.current);
  }, [mapReady]);

  // Add/update markers when cameras change
  useEffect(() => {
    if (!mapReady || !leafletMap.current) return;
    const L = window.L;

    // Clear existing markers
    markers.current.forEach(m => m.remove());
    markers.current = [];

    const withCoords = cameras.filter(c => c.lat && c.lng);
    const filtered   = filter
      ? withCoords.filter(c => c.name.toLowerCase().includes(filter.toLowerCase()) || c.location?.toLowerCase().includes(filter.toLowerCase()))
      : withCoords;

    filtered.forEach(cam => {
      const color  = cam.is_active ? '#10b981' : '#ef4444';
      const icon   = L.divIcon({
        className: '',
        html: `<div style="
          width:32px;height:32px;border-radius:50%;
          background:${color};border:3px solid white;
          display:flex;align-items:center;justify-content:center;
          font-size:14px;box-shadow:0 2px 8px rgba(0,0,0,0.3);
          cursor:pointer;
        ">📷</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -20],
      });

      const client = clients.find(cl => cl.id === cam.client_id);
      const marker = L.marker([cam.lat, cam.lng], { icon })
        .addTo(leafletMap.current)
        .bindPopup(`
          <div style="font-family:Arial;min-width:180px">
            <div style="font-weight:700;font-size:14px;margin-bottom:6px">📷 ${cam.name}</div>
            <div style="font-size:12px;color:#666;margin-bottom:4px">📍 ${cam.location || '—'}</div>
            <div style="font-size:12px;color:#666;margin-bottom:4px">🏢 ${client?.company || client?.name || '—'}</div>
            <div style="font-size:12px;color:#666;margin-bottom:4px">🌐 ${cam.ip_address}</div>
            <div style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;
              background:${cam.is_active ? '#d1fae5' : '#fee2e2'};color:${cam.is_active ? '#065f46' : '#991b1b'}">
              ${cam.is_active ? '● Aktyvi' : '● Neaktyvi'}
            </div>
          </div>
        `)
        .on('click', () => setSelCam(cam));

      markers.current.push(marker);
    });

    // Fit bounds if cameras with coords exist
    if (filtered.length > 0 && !selCam) {
      const group = L.featureGroup(markers.current);
      leafletMap.current.fitBounds(group.getBounds().pad(0.2));
    }
  }, [cameras, clients, filter, mapReady]);

  // Fly to selected camera
  useEffect(() => {
    if (!selCam || !leafletMap.current || !selCam.lat) return;
    leafletMap.current.flyTo([selCam.lat, selCam.lng], 17, { duration: 1 });
  }, [selCam]);

  const withCoords    = cameras.filter(c => c.lat && c.lng);
  const withoutCoords = cameras.filter(c => !c.lat || !c.lng);
  const clientName    = cid => { const cl = clients.find(c => c.id === cid); return cl?.company || cl?.name || '—'; };

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div className="page-title">Kamerų žemėlapis</div>
          <div className="page-subtitle">{withCoords.length} iš {cameras.length} kamerų turi koordinates</div>
        </div>
        <input className="filter-input" placeholder="🔍 Ieškoti kameros..." value={filter} onChange={e => setFilter(e.target.value)} style={{ width:'100%', maxWidth:240 }} />
      </div>

      {withoutCoords.length > 0 && (
        <div className="card" style={{ marginBottom:16, background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)', padding:'12px 16px' }}>
          <div style={{ fontSize:13, color:'#f59e0b' }}>
            ⚠️ <strong>{withoutCoords.length} kamer{withoutCoords.length === 1 ? 'a' : 'os'}</strong> neturi koordinačių: {withoutCoords.map(c => c.name).join(', ')}.{' '}
            Pridėkite koordinates <strong>Kameros → Redaguoti</strong>.
          </div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:20 }} className="map-layout">
        {/* Map */}
        <div className="card" style={{ padding:0, overflow:'hidden', height:'clamp(300px, 50vw, 560px)' }}>
          {!mapReady && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color:'var(--text-muted)' }}>
              <span className="loading-spinner" />
            </div>
          )}
          <div ref={mapRef} style={{ width:'100%', height:'100%', display: mapReady ? 'block' : 'none' }} />
        </div>

        {/* Sidebar */}
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {/* Stats */}
          <div className="card" style={{ padding:'16px' }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:10 }}>📊 Statistika</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              {[
                { label:'Iš viso', value:cameras.length, color:'var(--text)' },
                { label:'Aktyvios', value:cameras.filter(c=>c.is_active).length, color:'#10b981' },
                { label:'Su koordinatėmis', value:withCoords.length, color:'#3b82f6' },
                { label:'Be koordinačių', value:withoutCoords.length, color:'#f59e0b' },
              ].map(s => (
                <div key={s.label} style={{ background:'var(--bg)', borderRadius:8, padding:'8px', textAlign:'center' }}>
                  <div style={{ fontSize:20, fontWeight:800, color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:10, color:'var(--text-muted)' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Selected camera */}
          {selCam && (
            <div className="card" style={{ padding:'14px' }}>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:10 }}>📷 {selCam.name}</div>
              {[
                ['📍', selCam.location || '—'],
                ['🏢', clientName(selCam.client_id)],
                ['🌐', selCam.ip_address],
                ['🗺️', selCam.lat ? `${Number(selCam.lat).toFixed(5)}, ${Number(selCam.lng).toFixed(5)}` : '—'],
              ].map(([icon, val]) => (
                <div key={icon} style={{ display:'flex', gap:8, fontSize:12, padding:'4px 0', borderBottom:'1px solid var(--border)' }}>
                  <span>{icon}</span><span style={{ color:'var(--text-muted)' }}>{val}</span>
                </div>
              ))}
              <button className="btn btn-secondary" style={{ width:'100%', justifyContent:'center', marginTop:10, fontSize:12 }} onClick={() => setSelCam(null)}>Uždaryti</button>
            </div>
          )}

          {/* Camera list */}
          <div className="card" style={{ padding:0, flex:1, maxHeight:300, overflow:'auto' }}>
            <div style={{ padding:'8px 14px', borderBottom:'1px solid var(--border)', fontSize:12, fontWeight:700 }}>
              Kamerų sąrašas
            </div>
            {cameras.map(cam => (
              <div key={cam.id} onClick={() => cam.lat && setSelCam(cam)}
                style={{ padding:'8px 14px', borderBottom:'1px solid var(--border)',
                  cursor: cam.lat ? 'pointer' : 'default',
                  background: selCam?.id === cam.id ? 'rgba(59,130,246,0.1)' : 'transparent',
                  opacity: cam.lat ? 1 : 0.5
                }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div className={`cam-dot ${cam.is_active ? 'online' : 'offline'}`} />
                  <div>
                    <div style={{ fontSize:12, fontWeight:500 }}>{cam.name}</div>
                    <div style={{ fontSize:10, color:'var(--text-muted)' }}>
                      {cam.lat ? `📍 ${Number(cam.lat).toFixed(4)}, ${Number(cam.lng).toFixed(4)}` : '⚠️ Nėra koordinačių'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 768px) {
          .map-layout { grid-template-columns: 1fr !important; }
          .page-header { flex-direction: column; gap: 10px; }
        }
      `}</style>
    </div>
  );
}